/* Shared machinery for the motion illustrations. Each demo builds its own elements inside an empty
   stage, positions them from the stage size, and animates them with the Web Animations API. */

export type DemoImpl<Context> = {
  /** Create the demo's elements inside the stage. */
  setup: (stage: HTMLElement) => Context
  /** The starting state, before anything has happened. */
  reset: (context: Context) => void
  /** The finished state, shown when motion is paused or reduced. */
  final: (context: Context) => void
  /** Animate from reset to final. Call `ok()` after each wait: it throws once a newer run has started. */
  play: (context: Context, ok: () => void) => Promise<void>
}

export type DemoControls = {
  /** Play from the start, or jump to the end when motion is off. */
  run: () => void
  /** Hold the starting state, ready to play. */
  pre: () => void
  /** Show the finished state. */
  fin: () => void
  dispose: () => void
}

/** The demos need the Web Animations API (absent in jsdom). */
export const supportsDemos = () => typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function'

export const createDemo = <Context>(
  stage: HTMLElement,
  impl: DemoImpl<Context>,
  canAnimate: () => boolean,
): DemoControls => {
  const context = impl.setup(stage)
  let generation = 0

  const stop = () => {
    generation += 1
    stage.getAnimations({ subtree: true }).forEach((animation) => animation.cancel())
  }

  return {
    run: () => {
      stop()
      const current = ++generation
      impl.reset(context)
      // Paused, reduced, or the stage is hidden (phones hide the motion studies): show the end.
      if (!canAnimate() || stage.clientWidth === 0) {
        impl.final(context)
        return
      }
      impl
        .play(context, () => {
          if (current !== generation) throw new Error('stale')
        })
        .catch(() => {
          // Superseded by a newer run, or by pausing.
        })
    },
    pre: () => {
      stop()
      impl.reset(context)
    },
    fin: () => {
      stop()
      impl.final(context)
    },
    dispose: () => {
      stop()
      stage.replaceChildren()
    },
  }
}

export const easeOut = 'cubic-bezier(.2,.8,.2,1)'
export const spring = 'cubic-bezier(.34,1.56,.64,1)'
export const easeIn = 'cubic-bezier(.55,0,.75,.2)'

export const animate = (element: Element, keyframes: Keyframe[], options: KeyframeAnimationOptions = {}) =>
  element.animate(keyframes, { duration: 500, easing: easeOut, fill: 'both', ...options })

export const nap = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export const addElement = (tag: 'div' | 'span', className: string, parent: Element, html?: string) => {
  const element = document.createElement(tag)
  element.className = className
  if (html !== undefined) element.innerHTML = html
  parent.appendChild(element)
  return element
}

export const stageSize = (stage: HTMLElement) => ({ width: stage.clientWidth, height: stage.clientHeight })

export type Point = [number, number]

export const px = (value: number) => `${value}px`
